import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchConstructorAnalytics, fetchConstructors, fetchConstructorDetail } from '@/lib/api';
import { getConstructorLogoUrl, getCoverageColor, getGradeColor, getSectorColor, getValidationColor, PILLAR_CONFIG } from '@/lib/constants';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Landmark, ShieldCheck, TrendingUp, Users, Zap, Building2, Rocket, Globe2, Trophy } from 'lucide-react';
import { LineChart, Line } from 'recharts';

const TEAM_ICONS = { Financial: Landmark, Energy: Zap, Corporate: Building2, SME: Rocket };

export default function ConstructorsTable({ onCompanyClick, refreshTick }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState({});
  const [teamAnalytics, setTeamAnalytics] = useState({});

  useEffect(() => {
    fetchConstructors({ completed_only: true })
      .then((data) => setTeams(data.teams || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshTick]);

  const handleExpand = async (team) => {
    if (expandedTeam === team.id) {
      setExpandedTeam(null);
      return;
    }
    setExpandedTeam(team.id);
    try {
      const requests = [];
      if (!teamMembers[team.id]) requests.push(fetchConstructorDetail(team.id, { completed_only: true }));
      if (!teamAnalytics[team.id]) requests.push(fetchConstructorAnalytics(team.id, { completed_only: true }));
      const responses = await Promise.all(requests);
      responses.forEach((response) => {
        if (response?.members) {
          setTeamMembers((prev) => ({ ...prev, [team.id]: response.members || [] }));
        } else if (response?.coverage_mix) {
          setTeamAnalytics((prev) => ({ ...prev, [team.id]: response }));
        }
      });
      if (teamMembers[team.id] && !teamAnalytics[team.id]) {
        const analytics = await fetchConstructorAnalytics(team.id, { completed_only: true });
        setTeamAnalytics((prev) => ({ ...prev, [team.id]: analytics }));
      }
      if (teamAnalytics[team.id] && !teamMembers[team.id]) {
        const detail = await fetchConstructorDetail(team.id, { completed_only: true });
        setTeamMembers((prev) => ({ ...prev, [team.id]: detail.members || [] }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 rounded-2xl border border-white/10 bg-[#0D2238] animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="constructors-table">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Constructor sustainability profiles</h2>
          <p className="mt-1 text-sm text-white/40">Sector-based constructor teams, enhanced with sustainability KPI infographics and live F1 constructor identity markers.</p>
        </div>
      </div>

      <div className="space-y-3">
        {teams.map((team, index) => {
          const TeamIcon = TEAM_ICONS[team.sector] || Building2;
          const color = team.color || getSectorColor(team.sector);
          const isExpanded = expandedTeam === team.id;
          const members = teamMembers[team.id] || [];
          const analytics = teamAnalytics[team.id];

          return (
            <motion.div
              key={team.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08, duration: 0.3 }}
            >
              <div
                className="overflow-hidden rounded-2xl border border-white/10 bg-[#0D2238] shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-colors duration-150 hover:border-white/20"
                style={{ borderLeftWidth: '4px', borderLeftColor: color }}
              >
                <button
                  onClick={() => handleExpand(team)}
                  data-testid="constructors-row-expand-button"
                  className="flex w-full items-center gap-4 p-5 text-left"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl font-mono text-lg font-bold" style={{ backgroundColor: `${color}20`, color }}>
                    {team.position}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <TeamIcon className="h-4 w-4" style={{ color }} />
                      <h3 className="text-base font-semibold text-white">{team.name}</h3>
                      <Badge variant="outline" className="text-[10px]" style={{ color, borderColor: `${color}40` }}>
                        {team.member_count} entries
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-4">
                      <span className="text-xs text-white/40">Best: <span className="text-white/70">{team.best_performer}</span></span>
                      <span className="text-xs text-white/40">Most Improved: <span className="text-white/70">{team.most_improved}</span></span>
                    </div>
                  </div>

                  <div className="hidden text-center sm:block">
                    <p className="text-[10px] uppercase tracking-wider text-white/30">Avg ESG</p>
                    <p className="font-mono text-2xl font-bold tabular-nums" style={{ color }}>{team.average_score}</p>
                  </div>

                  <div className="hidden text-center md:block">
                    <p className="text-[10px] uppercase tracking-wider text-white/30">Points</p>
                    <p className="font-mono text-lg font-bold text-white tabular-nums">{team.points}</p>
                  </div>

                  <div className="hidden gap-2 lg:flex">
                    {team.pillar_averages && PILLAR_CONFIG.map((p) => (
                      <div key={p.key} className="text-center">
                        <p className="text-[8px] uppercase tracking-wider text-white/25">{p.name.split(' ')[0]}</p>
                        <p className="font-mono text-xs font-bold tabular-nums" style={{ color: p.color }}>
                          {team.pillar_averages[p.key.replace('_score', '')] || '-'}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="hidden h-8 w-20 xl:block">
                    {team.historical_points && (
                      <LineChart width={80} height={32} data={team.historical_points}>
                        <Line type="monotone" dataKey="points" stroke={color} strokeWidth={1.5} dot={false} />
                      </LineChart>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-white/40" /> : <ChevronDown className="h-5 w-5 text-white/40" />}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-4 border-t border-white/5 px-5 py-4">
                        {analytics ? (
                          <>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="constructor-sustainability-kpi-card">
                                <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">Validated members</p>
                                <p className="mt-2 font-mono text-2xl font-bold text-white tabular-nums">{analytics.validated_member_count}</p>
                                <p className="text-xs text-white/40">{analytics.validation_rate}% of profile</p>
                              </div>
                              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                                <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">Coverage mix</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {Object.entries(analytics.coverage_mix || {}).map(([level, count]) => (
                                    <Badge key={level} className="border border-white/10 text-white" style={{ backgroundColor: getCoverageColor(level) }}>
                                      {level}: {count}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                                <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">Benchmark delta</p>
                                <p className="mt-2 font-mono text-2xl font-bold text-white tabular-nums">{analytics.benchmark_delta_vs_global > 0 ? '+' : ''}{analytics.benchmark_delta_vs_global}</p>
                                <p className="text-xs text-white/40">vs sector benchmark</p>
                              </div>
                              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                                <p className="text-[10px] uppercase tracking-[0.16em] text-white/35">Top market presence</p>
                                <div className="mt-2 space-y-1 text-xs text-white/60">
                                  {analytics.top_country_presence.slice(0, 2).map((item) => (
                                    <p key={item.country}><span className="text-white">{item.country}</span> · {item.count}</p>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                                  <Globe2 className="h-4 w-4 text-[#3498DB]" />
                                  Region and country footprint
                                </div>
                                <div className="flex flex-wrap gap-2 text-xs text-white/65">
                                  {analytics.top_country_presence.map((item) => (
                                    <Badge key={item.country} className="border border-white/10 bg-white/5 text-white/80">
                                      {item.country} · {item.count}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                                  <Trophy className="h-4 w-4 text-[#D8B45A]" />
                                  Current F1 constructor pulse
                                </div>
                                <div className="space-y-2">
                                  {(analytics.f1_constructor_snapshot || []).slice(0, 4).map((entry) => {
                                    const logoUrl = getConstructorLogoUrl(entry.name || entry.team_name);
                                    return (
                                      <div key={`${entry.name}-${entry.position}`} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
                                        <div className="flex items-center gap-2">
                                          {logoUrl ? <img src={logoUrl} alt={entry.name} className="h-5 w-5 object-contain opacity-90" /> : null}
                                          <div>
                                            <p className="font-medium text-white">{entry.name}</p>
                                            <p className="text-xs text-white/40">{entry.wins} wins</p>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <p className="font-mono text-white tabular-nums">P{entry.position}</p>
                                          <p className="text-xs text-[#D8B45A]">{entry.points} pts</p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : null}

                        {members.length > 0 ? (
                          <div className="space-y-2">
                            {members.map((member, memberIndex) => (
                              <button
                                key={member.id}
                                onClick={() => onCompanyClick(member)}
                                className="flex w-full items-center gap-3 rounded-xl border border-transparent bg-white/[0.03] px-3 py-2.5 text-left transition-colors duration-150 hover:border-white/10 hover:bg-white/[0.06]"
                              >
                                <span className="w-5 font-mono text-xs text-white/30">{memberIndex + 1}</span>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-medium text-white">{member.name}</p>
                                  <p className="text-[10px] text-white/40">{member.sub_sector} · {member.gp_country}</p>
                                </div>
                                <div className="hidden items-center gap-2 md:flex">
                                  <Badge className="border border-white/10 text-white" style={{ backgroundColor: getValidationColor(member.validation_status) }}>
                                    <ShieldCheck className="mr-1 h-3 w-3" />
                                    {member.validation_status.replace('_', ' ')}
                                  </Badge>
                                </div>
                                <span className="font-mono text-lg font-bold tabular-nums" style={{ color: getGradeColor(member.grade) }}>
                                  {member.overall_score}
                                </span>
                                <Badge className="text-[10px]" style={{ backgroundColor: `${getGradeColor(member.grade)}2E`, color: getGradeColor(member.grade) }}>
                                  {member.grade}
                                </Badge>
                                <div className="flex items-center gap-1">
                                  {member.improvement_pct > 0 ? <TrendingUp className="h-3 w-3 text-[#2A9D8F]" /> : <span className="w-3" />}
                                  <span className={`font-mono text-[10px] tabular-nums ${member.improvement_pct > 0 ? 'text-[#2A9D8F]' : 'text-white/30'}`}>
                                    {member.improvement_pct > 0 ? '+' : ''}{member.improvement_pct}%
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 py-4 text-white/30">
                            <Users className="h-4 w-4" />
                            <span className="text-sm">Loading members...</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
